import {
  useApplyImpersonation,
  useCurrentAccount,
  useCurrentImpersonation,
  useGrippContext,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import { StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';

export const Banner = () => {
  const { account } = useCurrentAccount();
  const impersonation = useCurrentImpersonation();
  const { cancel } = useApplyImpersonation();
  const { logger } = useGrippContext();

  // Single source of truth for the active impersonation is
  // useCurrentImpersonation — the same hook the settings-menu ImpersonationView
  // uses, so both surfaces agree on whether a session is active and on its
  // workspace/user labels.
  //
  // User impersonation is driven by the swapped access token, so userId is only
  // ever set while genuinely acting as another user.
  const isUserImpersonation =
    impersonation.isImpersonating && impersonation.userId !== undefined;

  // The workspace ("acting as Gripp Admin") banner must stay gated on a live
  // globalAdmin check: useCurrentImpersonation reports isImpersonating for a
  // regular user too (it treats their own workspace as the active one), so
  // without this guard the banner would show for everyone. It also suppresses a
  // transient mis-render while impersonating a user — the token-read that backs
  // userId resolves async, and until it does the hook falls back to the
  // impersonated user's own workspace.
  const isWorkspaceImpersonation =
    impersonation.isImpersonating &&
    !isUserImpersonation &&
    (account?.isGlobalAdmin() ?? false);

  // workspaceName/userName are optional on the hook and a stored workspace can
  // carry an empty name, so fall back to a readable label rather than leaving a
  // blank in the banner text. The Leave action stays available either way.
  const workspaceName =
    impersonation.workspaceName?.trim() ||
    (impersonation.isUnassigned ? 'an unassigned' : 'the selected');
  const userName = impersonation.userName?.trim() || 'another user';

  const onLeave = async () => {
    await cancel();
    window.location.reload();
  };

  const handleLeave = () => {
    onLeave().catch((error) => logger?.error(error, 'Failed to end impersonation'));
  };

  return (
    <>
      {isWorkspaceImpersonation && (
        <View style={styles.workspaceBanner}>
          <Text style={styles.workspaceBannerText}>
            You are acting as "Gripp Admin" in {workspaceName} workspace
          </Text>
          <Button
            style={styles.button}
            labelStyle={{ color: Colors.white }}
            onPress={handleLeave}
            mode="outlined"
          >
            Leave
          </Button>
        </View>
      )}
      {isUserImpersonation && (
        <View style={styles.workspaceBanner}>
          <Text style={styles.workspaceBannerText}>
            You are acting as {userName}
          </Text>
          <Button
            style={styles.button}
            labelStyle={{ color: Colors.white }}
            onPress={handleLeave}
            mode="outlined"
          >
            Leave
          </Button>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  workspaceBanner: {
    flexDirection: 'row',
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.blue,
  },
  workspaceBannerText: {
    textAlign: 'center',
    color: Colors.white,
    fontSize: 20,
  },
  button: {
    borderColor: Colors.white,
    borderWidth: 1,
    borderRadius: 6,
    marginHorizontal: 10,
    maxHeight: 40,
  },
});
